import { memo, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Flame, BookOpen, History, Compass, Pencil, Check, ArrowRight, Share2 } from "lucide-react";
import PageShell from "../components/PageShell";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { getBooks } from "../api";
import { getReadChapters, getStreak, setChaptersRead, getLast } from "../lib/storage";
import { shareJourney } from "../lib/shareJourney";
import { track } from "../lib/analytics";

const RANGES = [
  { label: "All time", days: 0 },
  { label: "Past year", days: 365 },
  { label: "Past 30 days", days: 30 },
];

const DAY = 86400000;

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid={`stat-card-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <Icon className="mb-2 h-4 w-4 text-primary" />
      <p className="font-serif-display text-2xl text-primary sm:text-3xl">{value}</p>
      <p className="font-ui mt-0.5 text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
    </div>
  );
}

// Compact book tile: name + % read + progress fill. Fully-read glows gold.
const BookTile = memo(function BookTile({ book, read, onOpen }) {
  const pct = Math.round((read / book.chapters) * 100);
  const full = read === book.chapters;
  return (
    <button
      onClick={() => onOpen(book)}
      data-testid={`coverage-book-${book.id}`}
      className={`rounded-xl p-3 text-left transition-all active:scale-[0.98] ${
        full
          ? "bg-[#E0B85C] text-[#3A2E12] shadow-[0_0_14px_rgba(224,184,92,0.45)]"
          : "border border-border bg-card/50 hover:border-primary/40"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={`font-ui truncate text-sm font-semibold ${full ? "" : "text-foreground/90"}`}>{book.name}</span>
        {full ? (
          <Check className="h-4 w-4 shrink-0" />
        ) : (
          <span className="font-ui shrink-0 text-xs font-semibold text-primary">{pct}%</span>
        )}
      </div>
      {!full && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted/40">
          <div className="h-full rounded-full bg-[#E0B85C]" style={{ width: `${pct}%` }} />
        </div>
      )}
    </button>
  );
});

export default function ReadingJourney() {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [range, setRange] = useState(0);
  const [edit, setEdit] = useState(false);
  const [readChapters, setReadChapters] = useState(() => getReadChapters());
  const [modalBook, setModalBook] = useState(null);

  useEffect(() => {
    getBooks().then(setBooks);
    track("feature_open", { feature: "Reading Journey" });
  }, []);

  const streak = getStreak();
  const last = getLast();
  const nameById = useMemo(() => Object.fromEntries(books.map((b) => [b.id, b.name])), [books]);
  const cutoff = range === 0 ? 0 : Date.now() - range * DAY;

  // 0 unread · 1 read earlier · 2 read in range
  const stateFor = (book, ch) => {
    const ts = readChapters[`${book}_${ch}`];
    if (!ts) return 0;
    return ts >= cutoff ? 2 : 1;
  };

  // lifetime read count per book (drives the tile %)
  const readByBook = useMemo(() => {
    const m = {};
    books.forEach((b) => {
      let n = 0;
      for (let c = 1; c <= b.chapters; c++) if (readChapters[`${b.id}_${c}`]) n++;
      m[b.id] = n;
    });
    return m;
  }, [books, readChapters]);

  const totals = useMemo(() => {
    let total = 0, inRange = 0, otTotal = 0, otRead = 0, ntTotal = 0, ntRead = 0;
    books.forEach((b) => {
      total += b.chapters;
      let r = 0;
      for (let c = 1; c <= b.chapters; c++) if (stateFor(b.id, c) === 2) r++;
      inRange += r;
      if (b.testament === "OT") { otTotal += b.chapters; otRead += r; }
      else { ntTotal += b.chapters; ntRead += r; }
    });
    const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
    const fmt = (a, b) => (a > 0 && pct(a, b) === 0 ? "<1%" : `${pct(a, b)}%`);
    return { total, inRange, overall: fmt(inRange, total), ot: fmt(otRead, otTotal), nt: fmt(ntRead, ntTotal) };
  }, [books, readChapters, range]); // eslint-disable-line

  const history = useMemo(() => {
    return Object.entries(readChapters)
      .filter(([, ts]) => range === 0 || ts >= cutoff)
      .map(([k, ts]) => { const [b, c] = k.split("_"); return { b, c: parseInt(c, 10), ts }; })
      .sort((a, x) => x.ts - a.ts);
  }, [readChapters, range]); // eslint-disable-line

  // resume: in-app last position, else most recently read/logged chapter
  const resume = useMemo(() => {
    if (last?.book) return { book: last.book, chapter: last.chapter, name: last.bookName || last.book };
    let bestKey = null, bestTs = 0;
    Object.entries(readChapters).forEach(([k, ts]) => { if (ts > bestTs) { bestTs = ts; bestKey = k; } });
    if (!bestKey) return null;
    const [b, c] = bestKey.split("_");
    return { book: b, chapter: parseInt(c, 10), name: nameById[b] || b };
  }, [last, readChapters, nameById]);

  // ---- handlers ----
  const toggleCell = (book, ch, value) => {
    if (!edit) { setModalBook(null); navigate(`/read/${book}/${ch}`); return; }
    setReadChapters({ ...setChaptersRead([[book, ch]], value) });
  };
  const markBook = (book, value) => {
    const pairs = Array.from({ length: book.chapters }, (_, i) => [book.id, i + 1]);
    setReadChapters({ ...setChaptersRead(pairs, value) });
    toast(value ? `Marked ${book.name} as read` : `Cleared ${book.name}`);
  };
  const markTestament = (t, value) => {
    const pairs = [];
    books.filter((b) => b.testament === t).forEach((b) => { for (let c = 1; c <= b.chapters; c++) pairs.push([b.id, c]); });
    setReadChapters({ ...setChaptersRead(pairs, value) });
    const name = t === "OT" ? "Old" : "New";
    toast(value ? `Marked the ${name} Testament as read` : `Cleared the ${name} Testament`);
  };
  const handleShare = async () => {
    const flags = [];
    books.forEach((b) => { for (let c = 1; c <= b.chapters; c++) flags.push(!!readChapters[`${b.id}_${c}`]); });
    const total = flags.length || 1;
    const readCount = flags.filter(Boolean).length;
    const pct = Math.round((readCount / total) * 100);
    track("journey_share", { pct, chapters: readCount });
    const res = await shareJourney({ readFlags: flags, total, overallPct: pct, chaptersRead: readCount, streak: streak.count });
    if (res === "copied") toast("Journey summary copied to clipboard");
    if (res === "failed") toast("Couldn't share right now");
  };

  const ot = books.filter((b) => b.testament === "OT");
  const nt = books.filter((b) => b.testament === "NT");
  const rangeLabel = RANGES.find((r) => r.days === range)?.label.toLowerCase();
  const finishedBooks = books.filter((b) => readByBook[b.id] === b.chapters).length;

  const Section = ({ title, t, list }) => (
    <div className="mb-8">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="font-serif-display text-xl text-foreground/90">{title}</h2>
        {edit && (
          <button
            onClick={() => markTestament(t, true)}
            data-testid={`mark-testament-${t}`}
            className="font-ui rounded-full bg-primary/15 px-3 py-1 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/25"
          >
            Mark all read
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
        {list.map((b) => <BookTile key={b.id} book={b} read={readByBook[b.id] || 0} onOpen={setModalBook} />)}
      </div>
    </div>
  );

  // modal chapter states (range-aware) for the magnified book
  const modalStates = modalBook
    ? Array.from({ length: modalBook.chapters }, (_, i) => stateFor(modalBook.id, i + 1))
    : [];
  const modalRead = modalStates.filter((s) => s !== 0).length;
  const modalFull = modalBook && modalRead === modalBook.chapters;

  return (
    <PageShell
      title="Reading Journey"
      subtitle="Watch Scripture light up as you read"
      icon={Compass}
      accent={{ a: "#E0B85C", b: "#5C8A6E", c: "#2E1F12" }}
      testid="reading-journey-page"
    >
      {/* jump back in */}
      {resume && (
        <button
          onClick={() => navigate(`/read/${resume.book}/${resume.chapter}`)}
          data-testid="jump-back-in"
          className="group mb-5 flex w-full items-center justify-between gap-4 rounded-2xl border border-primary/30 bg-primary/10 px-5 py-4 text-left transition-all hover:border-primary/50 hover:bg-primary/[0.16]"
        >
          <span className="flex min-w-0 items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
              <BookOpen className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="font-ui block text-xs font-semibold uppercase tracking-[0.2em] text-primary">Jump back in</span>
              <span className="font-serif-display block truncate text-lg leading-tight text-foreground">{resume.name} {resume.chapter}</span>
            </span>
          </span>
          <ArrowRight className="h-5 w-5 shrink-0 text-primary transition-transform group-hover:translate-x-1" />
        </button>
      )}

      {/* stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat icon={Sparkles} value={totals.overall} label="Bible read" />
        <Stat icon={BookOpen} value={totals.inRange} label="Chapters" />
        <Stat icon={Flame} value={streak.count} label="Day streak" />
        <div className="rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid="stat-card-testaments">
          <History className="mb-2 h-4 w-4 text-primary" />
          <div className="flex items-baseline justify-between gap-2">
            <span className="font-ui text-xs uppercase tracking-wider text-muted-foreground">Old Test.</span>
            <span className="font-serif-display text-xl text-primary sm:text-2xl" data-testid="stat-ot">{totals.ot}</span>
          </div>
          <div className="mt-1 flex items-baseline justify-between gap-2">
            <span className="font-ui text-xs uppercase tracking-wider text-muted-foreground">New Test.</span>
            <span className="font-serif-display text-xl text-primary sm:text-2xl" data-testid="stat-nt">{totals.nt}</span>
          </div>
        </div>
      </div>

      {/* controls */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="time-toggle">
          {RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setRange(r.days)}
              data-testid={`time-toggle-${r.days}`}
              className={`font-ui flex-shrink-0 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                range === r.days ? "bg-primary text-primary-foreground" : "bg-muted/50 text-muted-foreground hover:bg-muted"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleShare}
            data-testid="share-journey"
            className="font-ui inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted"
          >
            <Share2 className="h-3.5 w-3.5" /> Share
          </button>
          <button
            onClick={() => setEdit((e) => !e)}
            data-testid="toggle-edit-mode"
            className={`font-ui inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
              edit ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:bg-muted"
            }`}
          >
            {edit ? <><Check className="h-3.5 w-3.5" /> Done</> : <><Pencil className="h-3.5 w-3.5" /> Log past reading</>}
          </button>
        </div>
      </div>

      <p className="font-ui mt-3 text-xs text-muted-foreground" data-testid="journey-hint">
        {edit
          ? "Tap a book to open it and mark chapters, or use “Mark all read” to log reading you did elsewhere — like a paper Bible."
          : `Tap any book to see its chapters and jump to what you haven't read yet.${finishedBooks > 0 ? ` · ${finishedBooks} finished` : ""}`}
      </p>

      {/* book grid — New Testament first to encourage */}
      <div className="mt-6">
        <Section title="New Testament" t="NT" list={nt} />
        <Section title="Old Testament" t="OT" list={ot} />
      </div>

      {/* history */}
      <div className="mt-2 border-t border-border/40 pt-8">
        <h2 className="font-serif-display mb-4 text-xl text-foreground/90">
          Recently read {range !== 0 && <span className="font-ui text-sm text-muted-foreground">· {rangeLabel}</span>}
        </h2>
        {history.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card/60 p-8 text-center" data-testid="journey-empty">
            <BookOpen className="mx-auto mb-3 h-7 w-7 text-primary" />
            <p className="font-serif-display text-lg">Nothing here yet</p>
            <p className="font-ui mt-1 text-sm text-muted-foreground">Finish a chapter and it will light up here.</p>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="journey-history">
            {history.slice(0, 200).map((e, i) => (
              <button
                key={`${e.b}_${e.c}_${i}`}
                onClick={() => navigate(`/read/${e.b}/${e.c}`)}
                data-testid={`history-item-${e.b}-${e.c}`}
                className="flex w-full items-center justify-between rounded-xl border border-border/60 bg-card/40 p-4 text-left transition-colors hover:border-primary/40 hover:bg-muted/40"
              >
                <span className="font-serif-display flex items-center gap-2 text-lg text-primary">
                  <BookOpen className="h-4 w-4" /> {nameById[e.b] || e.b} {e.c}
                </span>
                <span className="font-ui text-xs text-muted-foreground">
                  {new Date(e.ts).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* magnified book modal */}
      <Dialog open={!!modalBook} onOpenChange={(o) => !o && setModalBook(null)}>
        <DialogContent className="font-ui max-h-[85vh] overflow-y-auto" data-testid="book-modal">
          {modalBook && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif-display flex items-center gap-2 text-2xl">{modalBook.name}</DialogTitle>
                <DialogDescription className="font-ui">
                  {modalRead} of {modalBook.chapters} chapters read{modalFull ? " · complete 🎉" : ""}
                </DialogDescription>
              </DialogHeader>

              {edit && (
                <button
                  onClick={() => markBook(modalBook, !modalFull)}
                  data-testid={`mark-book-${modalBook.id}`}
                  className={`font-ui mb-1 w-full rounded-xl py-2.5 text-sm font-semibold transition-colors ${
                    modalFull ? "bg-muted text-muted-foreground hover:bg-muted/70" : "bg-primary/15 text-primary hover:bg-primary/25"
                  }`}
                >
                  {modalFull ? "Clear this book" : "Mark whole book as read"}
                </button>
              )}

              <p className="font-ui mb-2 mt-1 text-xs text-muted-foreground">
                {edit ? "Tap a chapter to mark it read or unread." : "Gold = read. Tap a chapter to open it."}
              </p>
              <div className="flex flex-wrap gap-1.5 pb-1">
                {modalStates.map((state, i) => {
                  const ch = i + 1;
                  const cls =
                    state === 2
                      ? "bg-[#E0B85C] text-[#4A3B18] shadow-[0_0_8px_rgba(224,184,92,0.55)]"
                      : state === 1
                      ? "bg-[#E0B85C]/20 border border-[#E0B85C]/15 text-[#E0B85C]/60"
                      : "bg-muted/30 border border-border/50 text-muted-foreground/50";
                  return (
                    <button
                      key={ch}
                      onClick={() => toggleCell(modalBook.id, ch, state === 0)}
                      data-testid={`coverage-cell-${modalBook.id}-${ch}`}
                      className={`flex h-7 w-7 items-center justify-center rounded-md text-[10px] font-ui transition-transform active:scale-90 ${cls}`}
                    >
                      {ch}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
